/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ReactElement, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, Plus, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/spinner';
import { CollapsibleLoadingContext } from './collapsible-loading-context';

export interface CollapsibleSectionProps {
    /** The label rendered inside the summary row */
    label: string;
    /** Optional content rendered after the label (e.g. AI badge) */
    labelSupplement?: ReactNode;
    /** Content revealed when the section is open */
    children: ReactNode;
    /** Whether the section starts open. Defaults to false. */
    defaultOpen?: boolean;
    /** Additional classes forwarded to the outer <details> element */
    className?: string;
    /**
     * Visual style. `'default'` is the plain bordered-row look used by FAQ etc.
     * `'pdp'` is the PDP Description/Product Details look: rounded dark-gray header
     * with a left-aligned +/- indicator and a white bordered content panel.
     * Defaults to `'default'`.
     */
    variant?: 'default' | 'pdp';
}

/**
 * A native HTML `<details>`/`<summary>` collapsible section.
 *
 * Children are lazy-mounted: they are not rendered until the section is opened
 * for the first time (or immediately if `defaultOpen` is true). Once mounted,
 * children remain in the DOM so their state (e.g. fetched data) is preserved
 * across subsequent open/close cycles.
 *
 * The `open` attribute is React-controlled. Clicking the summary mounts children
 * and sets a pending flag but does NOT expand the section immediately. The section
 * only opens once `isLoading` is false, preventing any layout shift from async
 * children (e.g. ProductAdapterSection). For synchronous children the section
 * opens on the same tick since `isLoading` is never set.
 *
 * While a child signals loading via `CollapsibleLoadingContext`, the chevron
 * icon is replaced with a spinner.
 *
 * The open/close transition animates `grid-template-rows` (0fr -> 1fr) on an
 * `overflow-hidden` wrapper around the content — a CSS-only technique that works
 * with `<details>` because the element is kept structurally `open` (via `hasOpened`)
 * once a section has ever been expanded; only the *visual* collapse is driven by
 * `isOpen` from there on. The collapsed content is marked `aria-hidden` and the
 * summary carries an explicit `aria-expanded` since the native `open` attribute no
 * longer reflects the visual state on its own.
 */
export default function CollapsibleSection({
    label,
    labelSupplement,
    children,
    defaultOpen = false,
    className,
    variant = 'default',
}: CollapsibleSectionProps): ReactElement {
    const isPdp = variant === 'pdp';
    // Whether the section is visually open (controls the open attribute).
    const [isOpen, setIsOpen] = useState(defaultOpen);
    // Whether children have been mounted at least once (for lazy mounting).
    const [hasOpened, setHasOpened] = useState(defaultOpen);
    // Set to true when the user clicks to open while content is still loading.
    const [pendingOpen, setPendingOpen] = useState(false);
    // Rendered loading state — drives the spinner visibility.
    const [isLoading, setIsLoadingState] = useState(false);
    // Ref mirror of isLoading. Written synchronously inside setLoading so the
    // open-decision effect (below) can read the true current value without
    // waiting for a child's queued state update to commit. This works because
    // React flushes children's effects before parents', so by the time the
    // parent effect reads the ref, the child has already written to it.
    const isLoadingRef = useRef(false);

    const loadingContextValue = useMemo(
        () => ({
            setLoading: (loading: boolean) => {
                isLoadingRef.current = loading;
                setIsLoadingState(loading);
            },
        }),
        [] // setIsLoadingState is a stable state setter — no deps needed
    );

    // Open the section once both conditions hold: the user requested it
    // (pendingOpen) and no child is loading. Reading the ref rather than the
    // isLoading state ensures we see the child's synchronous write even before
    // its queued state update has re-rendered. isLoading is still listed in
    // deps so this effect re-runs when loading state changes.
    useEffect(() => {
        if (pendingOpen && !isLoadingRef.current) {
            setIsOpen(true);
            setPendingOpen(false);
        }
    }, [pendingOpen, isLoading]); // isLoading re-triggers this effect when the child finishes loading

    const handleSummaryClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        if (isOpen) {
            setIsOpen(false);
        } else {
            setHasOpened(true);
            setPendingOpen(true);
        }
    };

    const indicator =
        isLoading || pendingOpen ? (
            <Spinner
                size="sm"
                className={cn('shrink-0', isPdp && 'border-primary-foreground/30 border-t-primary-foreground')}
            />
        ) : isPdp ? (
            isOpen ? (
                <Minus aria-hidden="true" className="size-5 shrink-0 text-primary-foreground" />
            ) : (
                <Plus aria-hidden="true" className="size-5 shrink-0 text-primary-foreground" />
            )
        ) : (
            <ChevronDownIcon
                aria-hidden="true"
                className="text-muted-foreground pointer-events-none size-5 shrink-0 translate-y-0.5 transition-transform duration-200 group-data-[state=open]:rotate-180"
            />
        );

    return (
        <CollapsibleLoadingContext value={loadingContextValue}>
            <details
                className={cn(
                    'group',
                    isPdp ? 'rounded-md border border-brand-gray-600 overflow-hidden' : 'border-b border-border',
                    className
                )}
                data-state={isOpen ? 'open' : 'closed'}
                open={hasOpened || undefined}>
                <summary
                    className={cn(
                        'flex items-center gap-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden transition-colors',
                        isPdp
                            ? // Full-width dark-gray header, left-aligned +/- indicator, white bold label.
                              // Negative outline-offset keeps the focus ring inside the rounded/clipped header.
                              'justify-start p-4 font-sans-bold text-primary-foreground bg-brand-gray-600 hover:bg-brand-gray-700 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary-foreground'
                            : 'justify-between gap-4 py-4 text-base font-medium text-foreground hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground'
                    )}
                    aria-expanded={isOpen}
                    onClick={handleSummaryClick}>
                    {isPdp ? (
                        <>
                            {indicator}
                            <span className="flex items-center gap-2">
                                {label}
                                {labelSupplement}
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="flex items-center gap-2">
                                {label}
                                {labelSupplement}
                            </span>
                            {indicator}
                        </>
                    )}
                </summary>
                {/* grid-rows 0fr/1fr trick: animates height without knowing the content's natural height. */}
                <div
                    className={cn(
                        'grid transition-[grid-template-rows] duration-300 ease-in-out',
                        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    )}
                    aria-hidden={!isOpen}>
                    <div className="overflow-hidden">
                        <div className={isPdp ? 'p-4 bg-background' : 'pb-4'}>{hasOpened && children}</div>
                    </div>
                </div>
            </details>
        </CollapsibleLoadingContext>
    );
}
